import React from 'react';
import { motion } from 'framer-motion';
import {
  Toast,
  resolveValue,
  toast as hotToast,
  useToaster,
  Toaster,
} from 'react-hot-toast';
import {
  X,
  Bot,
  UserMinus,
  UserCheck,
  RefreshCw,
  Zap,
  Loader2,
  Clock,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  Wifi,
  Volume2,
  VolumeX,
  RotateCcw,
  Sparkles,
  Info,
  LogOut,
} from 'lucide-react';

export interface CardSharksToastProps {
  toast: Toast;
}

interface ParsedToast {
  title: string;
  subtitle: React.ReactNode;
  iconNode: React.ReactNode;
  variant: 'amber' | 'rose' | 'emerald';
}

/**
 * Extracts and maps emoji to specialized Lucide icons or clean element
 */
function resolveIconElement(icon: any, fallbackNode: React.ReactNode): React.ReactNode {
  if (React.isValidElement(icon)) {
    return icon;
  }
  if (typeof icon === 'string') {
    const trimmed = icon.trim();
    switch (trimmed) {
      case '👋':
        return <UserMinus size={18} />;
      case '🚪':
        return <LogOut size={18} />;
      case '🔄':
        return <RefreshCw size={18} className="animate-spin" />;
      case '⏳':
        return <Loader2 size={18} className="animate-spin" />;
      case '🤖':
        return <Bot size={18} />;
      case '⏱️':
      case '⏰':
        return <Clock size={18} />;
      case '⚠️':
        return <AlertTriangle size={18} />;
      case '📶':
        return <Wifi size={18} />;
      case '🔇':
        return <VolumeX size={18} />;
      case '👂':
      case '🔊':
        return <Volume2 size={18} />;
      case '↺':
        return <RotateCcw size={18} />;
      case '⚡':
        return <Zap size={18} />;
      case 'ℹ️':
        return <Info size={18} />;
      case '✨':
        return <Sparkles size={18} />;
      default:
        return <span className="text-[16px] leading-none select-none">{trimmed}</span>;
    }
  }
  return fallbackNode;
}

/**
 * Parses any incoming toast message string or element into structured title,
 * subtitle, icon, and variant matching the Arena reclaiming seat HUD aesthetic.
 */
export function parseToastData(toast: Toast): ParsedToast {
  const resolved = resolveValue(toast.message, toast);
  const type = toast.type;

  // 1. If message is already a custom React element, wrap gracefully
  if (React.isValidElement(resolved)) {
    const variant = type === 'error' ? 'rose' : type === 'success' ? 'emerald' : 'amber';
    const defaultIcon =
      type === 'error' ? (
        <AlertCircle size={18} />
      ) : type === 'success' ? (
        <CheckCircle2 size={18} />
      ) : type === 'loading' ? (
        <Loader2 size={18} className="animate-spin" />
      ) : (
        <Zap size={18} />
      );

    return {
      title: type === 'error' ? 'ALERT' : type === 'success' ? 'SUCCESS' : 'TABLE NOTIFICATION',
      subtitle: resolved,
      iconNode: resolveIconElement(toast.icon, defaultIcon),
      variant,
    };
  }

  let text = String(resolved ?? '').trim();

  // Strip leading emoji prefixes while detecting their intention
  let leadingEmoji: string | null = null;
  const emojiMatch = text.match(/^([🚀⚡⚠️🔄👋🚪👀👁️⏱️⏰🤖⏳📶🔇👂🔊↺ℹ️✨✅❌]+)\s*/u);
  if (emojiMatch) {
    leadingEmoji = emojiMatch[1];
    text = text.slice(emojiMatch[0].length).trim();
  }

  let variant: 'amber' | 'rose' | 'emerald' =
    type === 'error' ? 'rose' : type === 'success' ? 'emerald' : 'amber';

  let title = '';
  let subtitle: React.ReactNode = null;
  let iconNode: React.ReactNode = null;

  // A. Specific presence: "Jon left the game", "Player left the table", "Guest stopped watching"
  const leftMatch = text.match(/^(.*?)\s+(left the game|left the table|left the room|left the match|stopped watching)/i);
  if (leftMatch) {
    const name = leftMatch[1].trim() || 'Player';
    const action = leftMatch[2];
    const isWatch = action.toLowerCase().includes('watching');
    title = isWatch ? 'SPECTATOR LEFT' : 'PLAYER LEFT';
    subtitle = (
      <span>
        <strong className="text-amber-300 font-bold">{name}</strong> {action}
      </span>
    );
    iconNode = <UserMinus size={18} />;
    variant = 'amber';
  }

  // B. Specific join/presence: "Alice joined the lobby", "Bob is now watching the table"
  const joinMatch = text.match(/^(.*?)\s+(joined the lobby|joined the room|joined the table|joined the game|is now watching the table|is now watching)/i);
  if (!title && joinMatch) {
    const name = joinMatch[1].trim() || 'Player';
    const action = joinMatch[2];
    const isWatch = action.toLowerCase().includes('watching');
    title = isWatch ? 'SPECTATOR JOINED' : 'PLAYER JOINED';
    subtitle = (
      <span>
        <strong className="text-amber-300 font-bold">{name}</strong> {action}
      </span>
    );
    iconNode = <UserCheck size={18} />;
    variant = 'amber';
  }

  // C. Server update / restart sync
  if (!title && /game server updated/i.test(text)) {
    title = 'GAME SERVER UPDATED';
    subtitle = text.replace(/^game server updated:?\s*/i, '') || 'Resyncing game state...';
    iconNode = <RefreshCw size={18} className="animate-spin" />;
    variant = 'amber';
  } else if (!title && /server updating/i.test(text)) {
    title = 'SERVER UPDATING';
    subtitle = text.replace(/^server updating\.{0,3}\s*/i, '') || 'Reconnecting to game table...';
    iconNode = <Zap size={18} />;
    variant = 'amber';
  }

  // D. Common game prompts & table action alerts
  if (!title) {
    if (/wait for your turn to play/i.test(text)) {
      title = 'NOT YOUR TURN';
      subtitle = 'Please wait for your turn to play';
      iconNode = <Clock size={18} />;
    } else if (/already selected 6 discards/i.test(text)) {
      title = 'DISCARD LIMIT';
      subtitle = 'You have already selected 6 cards to discard';
      iconNode = <AlertTriangle size={18} />;
    } else if (/all jokers must be discarded/i.test(text)) {
      title = 'NO-TRUMP RULES';
      subtitle = 'In No-Trump, all Jokers must be discarded into the kitty!';
      iconNode = <AlertTriangle size={18} />;
      variant = 'rose';
    } else if (/cleared invalid discard selection/i.test(text)) {
      title = 'INVALID DISCARDS';
      subtitle = text;
      iconNode = <AlertTriangle size={18} />;
      variant = 'rose';
    } else if (/syncing table/i.test(text)) {
      title = 'SYNCHRONIZING';
      subtitle = 'Syncing table state... please wait';
      iconNode = <Loader2 size={18} className="animate-spin" />;
    } else if (/series record reset/i.test(text)) {
      title = 'SERIES RESET';
      subtitle = text;
      iconNode = <RotateCcw size={18} />;
    } else if (/all remaining tricks claimed/i.test(text)) {
      title = 'TRICKS CLAIMED';
      subtitle = 'All remaining tricks claimed!';
      iconNode = <Sparkles size={18} />;
      variant = 'amber';
    } else if (/filled open seats with bots/i.test(text)) {
      title = 'BOTS SUBBED';
      subtitle = 'Filled open seats with tournament bots!';
      iconNode = <Bot size={18} />;
    } else if (/bot is playing for you/i.test(text)) {
      title = 'BOT IN SEAT';
      subtitle = 'Bot is playing for you! Return anytime to resume.';
      iconNode = <Bot size={18} />;
    } else if (/ai bot subbed in for/i.test(text)) {
      title = 'BOT SUBBED';
      subtitle = text;
      iconNode = <Bot size={18} />;
    } else if (/weak connection detected/i.test(text)) {
      title = 'WEAK CONNECTION';
      subtitle = 'High latency detected. Try switching to cellular data if lagging.';
      iconNode = <Wifi size={18} />;
    } else if (/audio routed to/i.test(text)) {
      title = 'AUDIO ROUTED';
      subtitle = text;
      iconNode = <Volume2 size={18} />;
    } else if (/microphone muted/i.test(text)) {
      title = 'MIC MUTED';
      subtitle = 'Microphone has been muted';
      iconNode = <VolumeX size={18} />;
    } else if (/deafened/i.test(text)) {
      title = 'DEAFENED';
      subtitle = 'Incoming voice audio muted';
      iconNode = <VolumeX size={18} />;
    } else if (/voice chat disabled/i.test(text)) {
      title = 'VOICE DISABLED';
      subtitle = 'Voice chat is currently turned off';
      iconNode = <VolumeX size={18} />;
    }
  }

  // E. Em-dash separator (e.g. "Time expired — auto-passed bid")
  if (!title && text.includes(' — ')) {
    const parts = text.split(' — ');
    title = parts[0].trim().toUpperCase();
    subtitle = parts.slice(1).join(' — ').trim();
  }

  // F. Colon separator (e.g. "Server Status: Connected")
  if (!title) {
    const colonMatch = text.match(/^([A-Za-z0-9\s]{3,24}):\s+(.+)$/);
    if (colonMatch) {
      title = colonMatch[1].trim().toUpperCase();
      subtitle = colonMatch[2].trim();
    }
  }

  // Fallback defaults
  if (!title) {
    if (type === 'error') {
      title = 'ALERT';
    } else if (type === 'success') {
      title = 'SUCCESS';
    } else if (type === 'loading') {
      title = 'PLEASE WAIT';
    } else {
      title = 'TABLE ALERT';
    }
    subtitle = text;
  }

  // Determine fallback icon if not explicitly set
  const fallbackIcon =
    variant === 'rose' ? (
      <AlertTriangle size={18} />
    ) : variant === 'emerald' ? (
      <CheckCircle2 size={18} />
    ) : type === 'loading' ? (
      <Loader2 size={18} className="animate-spin" />
    ) : (
      <Zap size={18} />
    );

  const effectiveIcon = resolveIconElement(toast.icon || leadingEmoji, iconNode || fallbackIcon);

  return {
    title,
    subtitle,
    iconNode: effectiveIcon,
    variant,
  };
}

/**
 * CardSharksToast
 *
 * Renders all toast notifications with the vibrant, pill-shaped aesthetic
 * matching the reclaim, seat, sadness, and celebratory pills.
 */
export const CardSharksToast: React.FC<CardSharksToastProps> = ({ toast }) => {
  const { title, subtitle, iconNode, variant } = parseToastData(toast);

  // Variant styling configurations matching the vibrant celebration & sadness pills
  const variantStyles = {
    amber: {
      bg: 'bg-gradient-to-r from-amber-500 via-amber-600 to-orange-600',
      border: 'border-2 border-amber-600/80',
      shadow: 'shadow-[0_8px_25px_rgba(245,158,11,0.45),0_2px_8px_rgba(0,0,0,0.35)]',
      badge: 'bg-black/20 text-white',
      suit: 'text-amber-100/90',
      title: 'text-white',
      subtitle: 'text-amber-50/95',
    },
    rose: {
      bg: 'bg-gradient-to-r from-red-500 via-rose-600 to-red-700',
      border: 'border-2 border-red-700/80',
      shadow: 'shadow-[0_8px_25px_rgba(244,63,94,0.45),0_2px_8px_rgba(0,0,0,0.35)]',
      badge: 'bg-black/20 text-white',
      suit: 'text-rose-100/90',
      title: 'text-white',
      subtitle: 'text-rose-50/95',
    },
    emerald: {
      bg: 'bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-700',
      border: 'border-2 border-emerald-600/80',
      shadow: 'shadow-[0_8px_25px_rgba(16,185,129,0.45),0_2px_8px_rgba(0,0,0,0.35)]',
      badge: 'bg-black/20 text-white',
      suit: 'text-emerald-100/90',
      title: 'text-white',
      subtitle: 'text-emerald-50/95',
    },
  }[variant];

  return (
    <motion.div
      initial={{ opacity: 0, y: -72, scale: 0.86 }}
      animate={{
        opacity: toast.visible ? 1 : 0,
        y: toast.visible ? 0 : -72,
        scale: toast.visible ? 1 : 0.88,
      }}
      transition={
        toast.visible
          ? { type: 'spring', stiffness: 520, damping: 24, mass: 0.7 }
          : { duration: 0.22, ease: [0.32, 0, 0.67, 0] }
      }
      className={`relative z-[100] max-w-md w-auto px-4 sm:px-5 py-2 sm:py-2.5 rounded-full ${variantStyles.bg} ${variantStyles.border} ${variantStyles.shadow} backdrop-blur-xl flex items-center justify-between gap-3 pointer-events-auto overflow-hidden before:absolute before:inset-x-6 before:top-0 before:h-[1px] before:bg-gradient-to-r before:from-transparent before:via-white/40 before:to-transparent cursor-pointer select-none text-white`}
      onClick={() => hotToast.dismiss(toast.id)}
      role="status"
    >
      <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
        {/* Sleek Frosted Icon Badge */}
        <div
          className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full border border-white/20 flex items-center justify-center shrink-0 ${variantStyles.badge} shadow-inner`}
        >
          {iconNode}
        </div>

        {/* Text Content */}
        <div className="flex flex-col min-w-0 text-left">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span
              className={`font-sans text-xs font-black uppercase tracking-wider ${variantStyles.title} truncate leading-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]`}
            >
              {title}
            </span>
            <span className={`${variantStyles.suit} text-[11px] select-none shrink-0 drop-shadow-sm`}>
              ♠
            </span>
          </div>
          {subtitle && (
            <div className={`text-[11px] ${variantStyles.subtitle} font-sans truncate leading-tight mt-0.5 max-w-[260px] sm:max-w-xs drop-shadow-[0_1px_2px_rgba(0,0,0,0.25)]`}>
              {subtitle}
            </div>
          )}
        </div>
      </div>

      {/* Modern Circular Dismiss button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          hotToast.dismiss(toast.id);
        }}
        className="w-6 h-6 rounded-full bg-black/20 hover:bg-black/35 text-white/80 hover:text-white flex items-center justify-center transition-all border border-white/20 active:scale-95 shrink-0 ml-1 cursor-pointer"
        title="Dismiss"
        aria-label="Dismiss notification"
      >
        <X size={12} />
      </button>
    </motion.div>
  );
};

/**
 * CardSharksToaster
 *
 * Global Deduplicating Toaster for ATL Card Sharks.
 * - Deduplicates visible toasts by title and message content in real time.
 * - Caps simultaneous visible toasts to 2 to prevent screen cluttering in the Arena.
 * - Renders all toasts using the luxury CardSharksToast HUD format.
 */
export const CardSharksToaster: React.FC = () => {
  const { toasts } = useToaster();
  const TOAST_LIMIT = 2;

  React.useEffect(() => {
    const visibleToasts = toasts.filter((t) => t.visible);

    // 1. Deduplicate visible toasts by parsed title + subtitle/message key
    const seen = new Set<string>();
    for (const t of visibleToasts) {
      const parsed = parseToastData(t);
      const subStr = typeof parsed.subtitle === 'string' ? parsed.subtitle : '';
      const key = `${parsed.title}:${subStr || String(t.message || '')}`.toLowerCase().trim();

      if (seen.has(key)) {
        hotToast.dismiss(t.id);
      } else {
        seen.add(key);
      }
    }

    // 2. Limit maximum concurrent visible toasts
    visibleToasts
      .filter((_, i) => i >= TOAST_LIMIT)
      .forEach((t) => hotToast.dismiss(t.id));
  }, [toasts]);

  return (
    <Toaster
      position="top-center"
      containerStyle={{
        top: 12,
        zIndex: 9999,
      }}
      gutter={8}
    >
      {(t) => <CardSharksToast toast={t} />}
    </Toaster>
  );
};

